import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  verifyPasswordResetCode,
  confirmPasswordReset,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  getAdditionalUserInfo,
  signInWithCredential,
  OAuthCredential,
  UserCredential,
} from 'firebase/auth';
import { auth, isFirebaseEnabled } from './firebaseConfig';
import { apiPost } from './apiClient';
import { Platform } from 'react-native';
import * as WebBrowser from 'expo-web-browser';
import * as Google from 'expo-auth-session/providers/google';

// Enable web browser completion for auth session
WebBrowser.maybeCompleteAuthSession();

// Google OAuth configuration
const GOOGLE_WEB_CLIENT_ID = process.env.EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID;
const GOOGLE_IOS_CLIENT_ID = process.env.EXPO_PUBLIC_GOOGLE_IOS_CLIENT_ID;
const GOOGLE_ANDROID_CLIENT_ID = process.env.EXPO_PUBLIC_GOOGLE_ANDROID_CLIENT_ID;

console.log('🔑 Google OAuth Config:', {
  webClientId: GOOGLE_WEB_CLIENT_ID ? `${GOOGLE_WEB_CLIENT_ID.substring(0, 20)}...` : 'MISSING',
  iosClientId: GOOGLE_IOS_CLIENT_ID ? 'Set' : 'MISSING',
  androidClientId: GOOGLE_ANDROID_CLIENT_ID ? 'Set' : 'MISSING',
});

/**
 * Detect iOS Safari (including PWA) - popups don't work reliably
 */
const isIOSSafari = (): boolean => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isWebKit = /WebKit/.test(ua);
  return isIOS && isWebKit;
};

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  isAnonymous: boolean;
}

/**
 * Result of a Google sign-in. Google is one button for both "create an
 * account" and "sign in", so the caller can't tell them apart from the user
 * alone — `isNewUser` carries Firebase's answer so analytics doesn't report
 * every returning sign-in as a fresh signup.
 */
export interface GoogleSignInResult {
  user: AuthUser;
  isNewUser: boolean;
}

/**
 * Convert Firebase User to AuthUser
 */
const mapFirebaseUser = (user: User): AuthUser => ({
  uid: user.uid,
  email: user.email,
  displayName: user.displayName,
  photoURL: user.photoURL,
  isAnonymous: user.isAnonymous,
});

/**
 * Convert a Firebase credential into a Google sign-in result. Defaults to
 * `false` when Firebase omits the additional-user-info block: under-reporting
 * a signup is better than announcing one that never happened.
 */
const mapGoogleCredential = (result: UserCredential): GoogleSignInResult => ({
  user: mapFirebaseUser(result.user),
  isNewUser: getAdditionalUserInfo(result)?.isNewUser === true,
});

/**
 * Sign in with email and password
 */
export const signInWithEmail = async (
  email: string,
  password: string
): Promise<AuthUser> => {
  if (!isFirebaseEnabled() || !auth) {
    throw new Error('Firebase not configured');
  }

  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  return mapFirebaseUser(userCredential.user);
};

/**
 * Identifier-first: does a Paprika account already exist for this email?
 * Routes the sign-in flow to "enter password" (true) vs "choose a password"
 * (false). Backed by /api/auth (Firebase Admin getUserByEmail);
 * throws on network/server error so the caller can show the offline banner.
 */
export const checkEmailExists = async (email: string): Promise<boolean> => {
  const res = await apiPost<{ exists: boolean }>('/api/ai/check-email', {
    email: email.trim().toLowerCase(),
  });
  return res.exists === true;
};

/** Which email went out: Du Chef's own, or Firebase's as the fallback. */
export type ResetEmailChannel = 'duchef' | 'firebase';

/**
 * Send a password-reset email. Callers must show a neutral confirmation
 * regardless of outcome — never reveal whether the address has an account.
 *
 * Du Chef sends its own email (`/api/ai/reset-password`, in the cook's
 * language, linking to `/auth/reset`). When that request fails for any reason
 * — offline API, Resend down, a tab on an Expo dev server that answers 200 +
 * HTML — Firebase's own email goes out instead, so the cook is never left
 * waiting for a message that was never sent.
 */
export const sendPasswordReset = async (email: string, language?: string): Promise<ResetEmailChannel> => {
  const address = email.trim();
  try {
    await apiPost('/api/ai/reset-password', { email: address, language }, { requireSuccess: true });
    return 'duchef';
  } catch (error) {
    console.warn('[auth] Du Chef reset email not sent, falling back to Firebase:', (error as Error)?.message);
  }
  if (!isFirebaseEnabled() || !auth) {
    throw new Error('Firebase not configured');
  }
  if (language) auth.languageCode = language;
  await sendPasswordResetEmail(auth, address);
  return 'firebase';
};

/**
 * The address a reset link was made for. Throws Firebase's
 * `auth/expired-action-code` / `auth/invalid-action-code` for a spent or
 * mistyped link (see `resetLinkErrorKind`).
 */
export const verifyResetCode = async (code: string): Promise<string> => {
  if (!isFirebaseEnabled() || !auth) {
    throw new Error('Firebase not configured');
  }
  return verifyPasswordResetCode(auth, code);
};

/** Spends the reset code on the new password. */
export const confirmNewPassword = async (code: string, password: string): Promise<void> => {
  if (!isFirebaseEnabled() || !auth) {
    throw new Error('Firebase not configured');
  }
  await confirmPasswordReset(auth, code, password);
};

/**
 * Register with email and password
 */
export const registerWithEmail = async (
  email: string,
  password: string
): Promise<AuthUser> => {
  if (!isFirebaseEnabled() || !auth) {
    throw new Error('Firebase not configured');
  }

  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  return mapFirebaseUser(userCredential.user);
};

/**
 * Check for redirect result after returning from Google auth
 * Call this on app initialization to handle the auth redirect flow
 */
export const checkRedirectResult = async (): Promise<GoogleSignInResult | null> => {
  if (!isFirebaseEnabled() || !auth) return null;

  try {
    console.log('🔐 Checking for redirect result...');
    const result = await getRedirectResult(auth);
    if (result?.user) {
      console.log('✅ Redirect auth successful:', result.user.email);
      return mapGoogleCredential(result);
    }
    console.log('🔐 No redirect result found');
  } catch (error: any) {
    console.error('❌ Redirect result error:', error);
  }
  return null;
};

/**
 * Sign in with Google (Web)
 * Uses redirect for iOS Safari (popup doesn't work), popup for other browsers
 */
const signInWithGoogleWeb = async (): Promise<GoogleSignInResult> => {
  if (!isFirebaseEnabled() || !auth) {
    console.error('🔥 Firebase not enabled or auth not initialized');
    throw new Error('Firebase not configured. Please check your environment variables.');
  }

  if (!GOOGLE_WEB_CLIENT_ID) {
    console.error('🔑 Missing EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID environment variable');
    throw new Error('Google Sign-In not configured. Please contact support.');
  }

  const provider = new GoogleAuthProvider();
  provider.addScope('profile');
  provider.addScope('email');

  // For iOS Safari: Try popup first (works on proper HTTPS domains like ngrok/Vercel)
  // Fall back to redirect only if popup is blocked
  if (isIOSSafari()) {
    console.log('🔐 iOS Safari detected - trying popup first...');
    try {
      const result = await signInWithPopup(auth, provider);
      console.log('✅ Google Sign-In popup successful on iOS Safari');
      return mapGoogleCredential(result);
    } catch (error: any) {
      // If popup is blocked or fails, fall back to redirect
      if (error.code === 'auth/popup-blocked' || error.code === 'auth/popup-closed-by-user') {
        console.log('🔐 Popup blocked/closed on iOS Safari, falling back to redirect...');
        try { sessionStorage.setItem('pendingOAuthRedirect', 'true'); } catch {}
        await signInWithRedirect(auth, provider);
        // This will redirect away from the app
        // The result will be handled by checkRedirectResult when user returns
        throw new Error('Redirecting to Google Sign-In...');
      }
      // Re-throw other errors
      throw error;
    }
  }

  // Use popup for other browsers
  console.log('🔐 Starting Google Sign-In with popup...');

  try {
    console.log('🔐 Opening Google Sign-In popup...');
    const result = await signInWithPopup(auth, provider);

    console.log('✅ Google Sign-In successful');
    console.log('✅ User email:', result.user.email);

    return mapGoogleCredential(result);
  } catch (error: any) {
    console.error('❌ Google Sign-In error:', error);
    console.error('❌ Error code:', error.code);
    console.error('❌ Error message:', error.message);

    // Provide user-friendly error messages
    if (error.code === 'auth/popup-blocked') {
      throw new Error('Popup was blocked by your browser. Please allow popups for this site and try again.');
    } else if (error.code === 'auth/popup-closed-by-user') {
      throw new Error('Sign-in was cancelled. Please try again.');
    } else if (error.code === 'auth/unauthorized-domain') {
      throw new Error('This domain is not authorized for Google Sign-In. Please contact support.');
    } else if (error.code === 'auth/operation-not-allowed') {
      throw new Error('Google Sign-In is not enabled. Please contact support.');
    } else {
      throw new Error(error.message || 'Google Sign-In failed. Please try again.');
    }
  }
};

/**
 * Sign in with Google (Mobile via expo-auth-session)
 */
const signInWithGoogleMobile = async (): Promise<GoogleSignInResult> => {
  if (!isFirebaseEnabled() || !auth) {
    throw new Error('Firebase not configured');
  }

  // This will be implemented with expo-auth-session
  // For now, throw not implemented
  throw new Error('Google Sign-In on mobile requires additional setup. Please use email/password for now.');
};

/**
 * Sign in with Google (cross-platform)
 */
export const signInWithGoogle = async (): Promise<GoogleSignInResult> => {
  if (Platform.OS === 'web') {
    return signInWithGoogleWeb();
  } else {
    return signInWithGoogleMobile();
  }
};

/**
 * Sign out
 */
export const signOut = async (): Promise<void> => {
  if (!isFirebaseEnabled() || !auth) {
    throw new Error('Firebase not configured');
  }

  await firebaseSignOut(auth);
};

/**
 * Get current user
 */
export const getCurrentUser = (): AuthUser | null => {
  if (!isFirebaseEnabled() || !auth || !auth.currentUser) {
    return null;
  }

  return mapFirebaseUser(auth.currentUser);
};

/**
 * Subscribe to auth state changes
 */
export const subscribeToAuthChanges = (
  callback: (user: AuthUser | null) => void
): (() => void) => {
  if (!isFirebaseEnabled() || !auth) {
    console.warn('Firebase not enabled, auth subscription not available');
    return () => {};
  }

  return onAuthStateChanged(auth, (firebaseUser) => {
    if (firebaseUser) {
      callback(mapFirebaseUser(firebaseUser));
    } else {
      callback(null);
    }
  });
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return isFirebaseEnabled() && auth !== null && auth.currentUser !== null;
};

/**
 * Wait for Firebase Auth to finish initializing from local cache.
 * Resolves in ~50-200ms on cached sessions (no network needed).
 * Much faster than waiting for onAuthStateChanged on startup.
 */
export const waitForAuthReady = async (): Promise<AuthUser | null> => {
  if (!isFirebaseEnabled() || !auth) return null;
  await auth.authStateReady();
  return auth.currentUser ? mapFirebaseUser(auth.currentUser) : null;
};

/**
 * Returns true if a Google OAuth redirect sign-in is in progress.
 * The flag is set before signInWithRedirect and cleared once getRedirectResult is processed.
 */
export const isOAuthRedirectPending = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return sessionStorage.getItem('pendingOAuthRedirect') === 'true';
  } catch {
    return false;
  }
};

/**
 * Clear the OAuth redirect pending flag after the result has been consumed.
 */
export const clearOAuthRedirectFlag = (): void => {
  try {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('pendingOAuthRedirect');
    }
  } catch {
    // ignore
  }
};
